import {
  SelectOptionOld,
  useModalOldContext,
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSForm,
  WSSelect
} from "@wingspanhq/fe-component-library";
import React from "react";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksCustomers,
  useQueryIntegrationsQuickbooksItems
} from "../../../../query/integrations/queries";
import { useMutationUpdatePayerEngagement } from "../../../../query/payerEngagements/mutations/useMutationUpdatePayerEngagement";
import { WSQueries } from "../../../../query/WSQuery";
import {
  INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT,
  QUICKBOOKS_ENTITY,
  RefreshModal
} from "../../../../Settings/screens/Integrations/quickbooks/RefreshButton";
import { selectQuickbooksAccountOrItemOptions } from "../../../Integrations/selectors/selectQuickbooksAccountOrItemOptions";
import { selectQuickbooksUserOptions } from "../../../Integrations/selectors/selectQuickbooksUserOptions";

type Props = {
  payerId: string;
  engagementId: string | undefined;
  itemId: string | undefined;
  customerId: string | undefined;
  onClose: () => void;
};

const EditPayerIntegrationModal: React.FC<Props> = ({
  onClose,
  payerId,
  engagementId,
  itemId,
  customerId
}) => {
  const qIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const qIntegrationsQuickbooksCustomers =
    useQueryIntegrationsQuickbooksCustomers();
  const qIntegrationsQuickbooksItems = useQueryIntegrationsQuickbooksItems();

  const [updateEngagement, updateEngagementMeta] =
    useMutationUpdatePayerEngagement();

  const { openModal, closeModal } = useModalOldContext();
  const { openSnackbar } = useWSSnackbar();

  return (
    <WSQueries
      queries={{
        qIntegrationsQuickbooks,
        qIntegrationsQuickbooksCustomers,
        qIntegrationsQuickbooksItems
      }}
    >
      {({
        qIntegrationsQuickbooks: { data: integrationsQuickbooks },
        qIntegrationsQuickbooksCustomers: {
          data: integrationsQuickbooksCustomers
        },
        qIntegrationsQuickbooksItems: { data: integrationsQuickbooksItems }
      }) => {
        const quickbooksCustomerOptions: SelectOptionOld[] =
          selectQuickbooksUserOptions(integrationsQuickbooksCustomers);

        const quickbooksItemsOptions: SelectOptionOld[] =
          selectQuickbooksAccountOrItemOptions(integrationsQuickbooksItems);

        const defaultItem = integrationsQuickbooksItems.find(
          item => item.itemId === integrationsQuickbooks.defaults?.itemId
        );

        return (
          <>
            <WSForm<{
              qboCustomerId?: string | undefined;
              qboItemId?: string | undefined;
            }>
              defaultValues={{
                qboCustomerId: customerId,
                qboItemId: itemId
              }}
              onSubmit={values => {
                if (engagementId) {
                  updateEngagement(
                    {
                      payerId,
                      engagementId,
                      payeeOwnedData: {
                        integration: {
                          quickbooks: {
                            customerId: values.qboCustomerId
                              ? values.qboCustomerId
                              : undefined,
                            itemId: values.qboItemId
                              ? values.qboItemId
                              : undefined
                          }
                        }
                      }
                    },
                    {
                      onSuccess: () => {
                        onClose();
                      }
                    }
                  );
                } else {
                  // TODO (v3-migration): QuickBooks mapping is NOT a Payer field by
                  // design (PLA-3198 #6) — it lives on the v3 AccountingConnection
                  // (/v3/platform/accounting-connections). Re-source from there
                  // rather than expecting a QBO field on Payer.
                  openSnackbar({
                    type: "warning",
                    duration: 5000,
                    message:
                      "Updating the QuickBooks mapping isn't available yet"
                  });
                  onClose();
                }
              }}
            >
              <WSForm.Field
                mb="XL"
                name="qboCustomerId"
                component={WSSelect}
                componentProps={{
                  placeholder: "Not mapped, create new Customer",
                  options: quickbooksCustomerOptions,
                  action: {
                    text: "Resync Customers",
                    // icon: "refresh-v",
                    onClick: () => {
                      openModal(INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT, {
                        entity: QUICKBOOKS_ENTITY.CUSTOMERS
                      });
                    }
                  }
                }}
                label="QBO Customer"
              />
              <WSForm.Field
                mb="2XL"
                name="qboItemId"
                component={WSSelect}
                componentProps={{
                  options: quickbooksItemsOptions,
                  placeholder: `Use default "${defaultItem?.fullyQualifiedName}"`,
                  action: {
                    text: "Resync Items",
                    // icon: "refresh-v",
                    onClick: () => {
                      openModal(INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT, {
                        entity: QUICKBOOKS_ENTITY.ITEMS
                      });
                    }
                  }
                }}
                label="Default QBO Item"
              />

              <WSErrorMessage
                mb="XL"
                error={updateEngagementMeta.error}
                contextKey="EditClientIntegrations"
              />

              <WSActions
                alignment="fill"
                buttons={[
                  {
                    label: "Update",
                    kind: "Primary",
                    type: "submit",
                    loading: updateEngagementMeta.isLoading
                  },
                  {
                    kind: "Secondary",
                    label: "Cancel",
                    onClick: onClose
                  }
                ]}
              />
            </WSForm>
            <RefreshModal />
          </>
        );
      }}
    </WSQueries>
  );
};

export const useEditPayerIntegrationModal = () =>
  useWSModal(EditPayerIntegrationModal, {
    title: "Edit integration",
    size: "S"
  });
